import { Add, ExpandMore } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogContent,
    FormControl,
    FormControlLabel,
    Grid,
    InputAdornment,
    InputLabel,
    Menu,
    MenuItem,
    OutlinedInput,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Tabs,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { SUPPLIER_INVOICE_STATUS_COLOR } from '../../../App';
import { getSupplierInvoiceListApi, getSupplierListApi, updateRecordPaymentApi } from '../../../redux/Supplier/action';
import { TableRowsLoader } from '../../../utility/Constant';
import { formatNumberAsWhole } from '../../../utility/helpers/commonFunction';
import { NumericEngine } from '../../../utility/helpers/numericEngine';
import RecordPaymentModal from './RecordPaymentModal';

const CustomTabPanel = ({ children, value, index, ...other }) => (
    <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
        {value === index && (
            <Box sx={{ pt: 4 }}>
                <Typography>{children}</Typography>
            </Box>
        )}
    </div>
);

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

const index = [0, 1, 2, 3, 4];

const SupplierInvoices = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [isRecordLoading, setIsRecordLoading] = useState(false);

    const [anchorElSupplier, setAnchorElSupplier] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [sortParams, setSortParams] = useState([
        {
            key: 'invoiceNumber',
            orderBy: 'DESC',
        },
        {
            key: 'supplierName',
            orderBy: 'DESC',
        },
        {
            key: 'date',
            orderBy: 'DESC',
        },

        {
            key: 'total',
            orderBy: 'DESC',
        },
    ]);
    const [searchValue, setSearchValue] = useState('');

    const [openPaymentModal, setOpenPaymentModal] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const [fullScreenLoader, setFullScreenLoader] = useState(false);
    const [showLoader, setShowLoader] = useState(false);

    const [dateData, setDateData] = useState('');
    const [value, setValue] = useState(0);
    const [selecetdSupplierId, setSelectedSupplierId] = useState([]);
    const nameArray = window.location.pathname.split('/');
    const currentPageStatus = nameArray[nameArray.length - 1];
    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(Number.parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDateChange = (date) => {
        const formattedDate = date ? dayjs(date).format('YYYY-MM-DD') : '';
        setDateData(formattedDate);
    };

    const supplierList = useSelector((state) => state?.supplier?.supplierData?.data);
    const supplierInvoiceList = useSelector((state) => state?.supplier?.supplierInvoiceList);

    const fetchSupplerList = useCallback(async () => {
        await dispatch(getSupplierListApi({ pagination: false }));
    }, [dispatch]);

    useEffect(() => {
        fetchSupplerList();
    }, [fetchSupplerList]);

    // Memoize the function with useCallback
    const fetchSupplierInvoice = useCallback(async () => {
        setShowLoader(true);
        const params = {
            pagination: true,
            page: page + 1,
            limit: rowsPerPage,
            search: searchValue.trim(),
            date: dateData,
            sort: encodeURIComponent(JSON.stringify(sortParams)),
        };
        if (currentPageStatus === 'all' || currentPageStatus === 'draft') {
            params.type = currentPageStatus;
        } else {
            params.paymentStatus = currentPageStatus === 'partial' ? 'partial_payment' : currentPageStatus === 'paid' ? 'paid_in_full' : currentPageStatus;
        }
        await dispatch(getSupplierInvoiceListApi(params, selecetdSupplierId));
        setShowLoader(false);
    }, [page, rowsPerPage, searchValue, dateData, sortParams, currentPageStatus, dispatch, selecetdSupplierId]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => fetchSupplierInvoice(), 300);
        return () => clearTimeout(delayDebounceFn);
    }, [fetchSupplierInvoice]);

    useEffect(() => {
        if (currentPageStatus === 'all') {
            setValue(0);
        }
        if (currentPageStatus === 'open') {
            setValue(1);
        }
        if (currentPageStatus === 'partail') {
            setValue(2);
        }
        if (currentPageStatus === 'paid') {
            setValue(3);
        }
        if (currentPageStatus === 'draft') {
            setValue(4);
        }
    }, [currentPageStatus]);

    const handleSortRequest = (key) => {
        setSortParams((prevSortParams) => {
            // Find the existing sort index
            const existingSortIndex = prevSortParams.findIndex((param) => param.key === key);

            const newSortParams = [...prevSortParams];

            if (existingSortIndex >= 0) {
                // Toggle the order if the key already exists
                const updatedSort = {
                    ...newSortParams[existingSortIndex],
                    orderBy: newSortParams[existingSortIndex].orderBy === 'ASC' ? 'DESC' : 'ASC',
                };

                // Remove from current position and add to the start
                newSortParams.splice(existingSortIndex, 1);
                newSortParams.unshift(updatedSort);
            } else {
                // Add new sort option to the top
                newSortParams.unshift({ key, orderBy: 'ASC' });
            }

            return newSortParams;
        });
    };

    return (
        <>
            <Box padding={'48px 24px 0px 24px'} sx={{ bgcolor: 'background.main' }}>
                <Grid container rowGap={2}>
                    <Grid container item justifyContent={'space-between'}>
                        <Grid item sm={4} lg={4}>
                            <Typography variant="h4" sx={{ fontWeight: 400 }}>
                                Supplier Invoices{' '}
                            </Typography>
                        </Grid>
                        <Grid container sm={8} lg={8} gap={3} justifyContent={'end'}>
                            <Grid>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<Add />}
                                    onClick={() => {
                                        navigate('/suppliers-invoice/new');
                                    }}
                                    className="btn-text-size-manage"
                                >
                                    NEW Supplier Invoice
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid
                        container
                        item
                        // gap={2}
                        margin={'12px 0'}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                    >
                        <Grid item container alignItems={'center'}>
                            <Box sx={{ width: '100%' }}>
                                <Box>
                                    <Tabs
                                        value={value}
                                        onChange={(event, newValue) => {
                                            setShowLoader(true);
                                            if (newValue === 0) {
                                                navigate('/suppliers-invoice/all');
                                            }
                                            if (newValue === 1) {
                                                navigate('/suppliers-invoice/open');
                                            }
                                            if (newValue === 2) {
                                                navigate('/suppliers-invoice/partial');
                                            }
                                            if (newValue === 3) {
                                                navigate('/suppliers-invoice/paid');
                                            }
                                            if (newValue === 4) {
                                                navigate('/suppliers-invoice/draft');
                                            }
                                            setValue(newValue);
                                        }}
                                        aria-label="basic tabs example"
                                    >
                                        <Tab label="ALL" {...a11yProps(0)} style={{ fontWeight: 500 }} />
                                        <Tab label="OPEN" {...a11yProps(1)} style={{ fontWeight: 500 }} />
                                        <Tab label="PARTIAL" {...a11yProps(2)} style={{ fontWeight: 500 }} />
                                        <Tab label="PAID" {...a11yProps(3)} style={{ fontWeight: 500 }} />
                                        <Tab label="DRAFT" {...a11yProps(4)} style={{ fontWeight: 500 }} />
                                    </Tabs>
                                </Box>
                                {index?.map((i) => {
                                    return (
                                        <CustomTabPanel value={value} key={i} index={i} padding={'24px'}>
                                            <Grid container item>
                                                <Grid container marginBottom={'32px'} alignItems={'center'} spacing={2} justifyContent={'start'}>
                                                    {/* Select Date */}
                                                    <Grid
                                                        item
                                                        xs={6}
                                                        sm={6}
                                                        md={3}
                                                        lg={2}
                                                        xl={2}
                                                        // padding={"6px"}
                                                    >
                                                        <Stack
                                                            direction={'row'}
                                                            alignItems={'center'}
                                                            display={'flex'}
                                                            justifyContent={'space-between'}
                                                            spacing={1}
                                                        >
                                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                                {/* <DatePicker
                                                                    className="DropDatePicker"
                                                                    label="SELECT DATE"
                                                                    name="proposeDate"
                                                                    value={dateData ? moment(dateData, 'YYYY-MM-DD') : null}
                                                                    onChange={handleDateChange}
                                                                    style={{ width: '100%' }}
                                                                    slotProps={{
                                                                        textField: {
                                                                            InputProps: {
                                                                                endAdornment: (
                                                                                    <InputAdornment position="end">
                                                                                        <ExpandMore
                                                                                           
                                                                                            style={{ cursor: 'pointer' }}
                                                                                        />
                                                                                    </InputAdornment>
                                                                                ),
                                                                            },
                                                                        },
                                                                        field: { clearable: true },
                                                                    }} */}
                                                                <DatePicker
                                                                    className="DropDatePicker"
                                                                    label="SELECT DATE"
                                                                    name="proposeDate"
                                                                    value={dateData ? dayjs(dateData, 'YYYY-MM-DD') : null}
                                                                    onChange={handleDateChange}
                                                                    slots={{
                                                                        openPickerIcon: ExpandMore,
                                                                    }}
                                                                    slotProps={{
                                                                        textField: {
                                                                            fullWidth: true,
                                                                            sx: {
                                                                                '& .MuiInputBase-input': {
                                                                                    fontSize: 14,
                                                                                    color: '#000',
                                                                                    fontWeight: 500,
                                                                                },
                                                                                '& .MuiInputLabel-root': {
                                                                                    fontSize: 14,
                                                                                    color: '#000',
                                                                                    fontWeight: 500,
                                                                                },
                                                                            },
                                                                        },
                                                                        field: {
                                                                            clearable: true,
                                                                        },
                                                                        openPickerIcon: {
                                                                            sx: {
                                                                                color: '#000', // ✅ Set icon color to black here
                                                                            },
                                                                        },
                                                                    }}
                                                                />
                                                            </LocalizationProvider>
                                                        </Stack>
                                                    </Grid>

                                                    {/* Select Vehicle */}
                                                    <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                                                        <Button
                                                            variant="text"
                                                            color="primary"
                                                            style={{
                                                                color: 'black',
                                                                fontWeight: 500,
                                                                lineHeight: '24px',
                                                            }}
                                                            onClick={(event) => {
                                                                setAnchorElSupplier(event.currentTarget);
                                                            }}
                                                            fullWidth
                                                            sx={{ padding: '0px!important' }}
                                                            endIcon={<ExpandMoreIcon />}
                                                            sty
                                                        >
                                                            {/* Display the selected supplier name or fallback to "SELECT SUPPLIER" */}
                                                            SELECT SUPPLIER
                                                        </Button>

                                                        <Menu
                                                            id="basic-menu"
                                                            anchorEl={anchorElSupplier}
                                                            open={Boolean(anchorElSupplier)}
                                                            onClose={() => setAnchorElSupplier(null)}
                                                            MenuListProps={{
                                                                'aria-labelledby': 'basic-button',
                                                            }}
                                                            style={{ height: '400px', width: '320px' }}
                                                        >
                                                            {supplierList &&
                                                                Array.isArray(supplierList.sort((a, b) => a?.name?.localeCompare(b?.name))) &&
                                                                supplierList.map((data) => (
                                                                    <MenuItem key={data.id}>
                                                                        <FormControlLabel
                                                                            control={
                                                                                <Checkbox
                                                                                    checked={selecetdSupplierId?.includes(data?._id)} // Check if data.id is in the state
                                                                                    onChange={() => {
                                                                                        setSelectedSupplierId(
                                                                                            (prev) =>
                                                                                                prev.includes(data?._id)
                                                                                                    ? prev.filter((_id) => _id !== data?._id) // Remove if already selected
                                                                                                    : [...prev, data?._id] // Add if not selected
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            }
                                                                            label={data.name}
                                                                        />
                                                                    </MenuItem>
                                                                ))}
                                                        </Menu>
                                                    </Grid>

                                                    {/* Select Search */}
                                                    <Grid item xs={12} sm={12} md={6} lg={8} xl={8} padding={'6px'}>
                                                        <FormControl fullWidth>
                                                            <InputLabel htmlFor="outlined-adornment-search">Search</InputLabel>
                                                            <OutlinedInput
                                                                id="outlined-adornment-search"
                                                                placeholder="Supplier name, invoice number, etc..."
                                                                onChange={(event) => {
                                                                    setSearchValue(event.target.value);
                                                                }}
                                                                startAdornment={
                                                                    <InputAdornment position="start">
                                                                        <SearchIcon />
                                                                    </InputAdornment>
                                                                }
                                                                label="Search"
                                                            />
                                                        </FormControl>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Box
                                                sx={{
                                                    bgcolor: 'white',
                                                    p: '24px',
                                                }}
                                                boxShadow={2}
                                            >
                                                <Grid container item>
                                                    {supplierInvoiceList?.data?.length === 0 && showLoader === false ? (
                                                        <div className="not-found-class">Sorry, we could not find any matches.</div>
                                                    ) : (
                                                        <Grid item xl={12} md={12} sm={12} xs={12}>
                                                            <TableContainer
                                                                sx={{
                                                                    width: '100%',
                                                                }}
                                                            >
                                                                <Table stickyHeader padding="16px" sx={{ tableLayout: 'fixed' }}>
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell style={{ width: '175px' }}>
                                                                                <TableSortLabel
                                                                                    active={sortParams.some((param) => param.key === 'invoiceNumber')}
                                                                                    direction={
                                                                                        sortParams.find((param) => param.key === 'invoiceNumber')?.orderBy ===
                                                                                        'ASC'
                                                                                            ? 'asc'
                                                                                            : 'desc'
                                                                                    }
                                                                                    onClick={() => handleSortRequest('invoiceNumber')}
                                                                                    sx={{ fontWeight: 500 }}
                                                                                >
                                                                                    Invoice Number
                                                                                </TableSortLabel>{' '}
                                                                            </TableCell>
                                                                            <TableCell style={{ width: '175px' }}>
                                                                                <TableSortLabel
                                                                                    active={sortParams.some((param) => param.key === 'supplierName')}
                                                                                    direction={
                                                                                        sortParams.find((param) => param.key === 'supplierName')?.orderBy ===
                                                                                        'ASC'
                                                                                            ? 'asc'
                                                                                            : 'desc'
                                                                                    }
                                                                                    onClick={() => handleSortRequest('supplierName')}
                                                                                >
                                                                                    Supplier Name
                                                                                </TableSortLabel>
                                                                            </TableCell>
                                                                            <TableCell style={{ width: '175px' }}>
                                                                                <TableSortLabel
                                                                                    active={sortParams.some((param) => param.key === 'date')}
                                                                                    direction={
                                                                                        sortParams.find((param) => param.key === 'date')?.orderBy === 'ASC'
                                                                                            ? 'asc'
                                                                                            : 'desc'
                                                                                    }
                                                                                    onClick={() => handleSortRequest('date')}
                                                                                >
                                                                                    Date
                                                                                </TableSortLabel>
                                                                            </TableCell>
                                                                            <TableCell style={{ width: '120px' }}>
                                                                                <TableSortLabel
                                                                                    active={sortParams.some((param) => param.key === 'total')}
                                                                                    direction={
                                                                                        sortParams.find((param) => param.key === 'total')?.orderBy === 'ASC'
                                                                                            ? 'asc'
                                                                                            : 'desc'
                                                                                    }
                                                                                    onClick={() => handleSortRequest('total')}
                                                                                >
                                                                                    Total
                                                                                </TableSortLabel>
                                                                            </TableCell>
                                                                            <TableCell sx={{ width: '150px' }}>
                                                                                <Typography fontWeight={500}>Payment Status</Typography>
                                                                            </TableCell>
                                                                            <TableCell sx={{ width: '150px' }}>
                                                                                <Typography fontWeight={500}>Invoice Status</Typography>
                                                                            </TableCell>
                                                                            <TableCell sx={{ width: '175px' }}>
                                                                                <Typography fontWeight={500}>Actions</Typography>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {showLoader ? (
                                                                            <TableRowsLoader rowsNum={rowsPerPage} />
                                                                        ) : (
                                                                            Array.isArray(supplierInvoiceList?.data) &&
                                                                            supplierInvoiceList?.data?.map((row) => (
                                                                                <TableRow
                                                                                    key={row._id}
                                                                                    sx={{ cursor: 'pointer' }}
                                                                                    hover={true}
                                                                                    onClick={() => {
                                                                                        if (currentPageStatus === 'all') {
                                                                                            if (row?.paymentStatus === 'paid_in_full') {
                                                                                                navigate(`/suppliers-invoice/paid/${row?._id}`);
                                                                                            } else if (row?.paymentStatus === 'partial_payment') {
                                                                                                navigate(`/suppliers-invoice/partial/${row?._id}`);
                                                                                            } else if (row?.paymentStatus === 'open') {
                                                                                                navigate(`/suppliers-invoice/open/${row?._id}`);
                                                                                            }
                                                                                        } else {
                                                                                            navigate(`/suppliers-invoice/${currentPageStatus}/${row?._id}`);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    <TableCell component="th" scope="row">
                                                                                        {row?.invoiceNumber}
                                                                                    </TableCell>
                                                                                    <TableCell align="left">{`${row?.supplier?.name}`}</TableCell>
                                                                                    <TableCell align="left">
                                                                                        {' '}
                                                                                        {moment(row?.supplierInvoiceDate).format('MMM D, YYYY')}
                                                                                    </TableCell>
                                                                                    <TableCell align="left">
                                                                                        {row?.totalAmount
                                                                                            ? NumericEngine.format(row?.totalAmount || 0, {
                                                                                                  formatCurrency: true,
                                                                                                  trailingZeros: true,
                                                                                              })
                                                                                            : '$0.00'}
                                                                                    </TableCell>
                                                                                    {/* paymentStatus  */}
                                                                                    <TableCell align="left" onClick={() => {}}>
                                                                                        {row?.paymentStatus && (
                                                                                            <Chip
                                                                                                label={
                                                                                                    row?.paymentStatus === 'paid_in_full'
                                                                                                        ? 'Paid'
                                                                                                        : row?.paymentStatus === 'partial_payment'
                                                                                                          ? 'Partial'
                                                                                                          : row?.paymentStatus === 'open'
                                                                                                            ? 'Open'
                                                                                                            : ''
                                                                                                }
                                                                                                style={{
                                                                                                    backgroundColor:
                                                                                                        SUPPLIER_INVOICE_STATUS_COLOR[row?.paymentStatus],
                                                                                                    // row?.paymentStatus ===
                                                                                                    // "paid_in_full"
                                                                                                    //   ? "#AFFD9C"
                                                                                                    //   : row?.paymentStatus ===
                                                                                                    //     "partial_payment"
                                                                                                    //   ? "#FFC7EC"
                                                                                                    //   : row?.paymentStatus ===
                                                                                                    //     "open"
                                                                                                    //   ? "#B0E8FC"
                                                                                                    //   : "",
                                                                                                    color: '#000000DE',
                                                                                                    fontWeight: '500',
                                                                                                }}
                                                                                            />
                                                                                        )}
                                                                                    </TableCell>
                                                                                    {/* Invoice Status  */}
                                                                                    <TableCell align="left" onClick={() => {}}>
                                                                                        <Chip
                                                                                            label={row?.isDraft ? 'Draft' : 'Finalized'}
                                                                                            style={{
                                                                                                backgroundColor: row?.isDraft
                                                                                                    ? SUPPLIER_INVOICE_STATUS_COLOR.draft
                                                                                                    : SUPPLIER_INVOICE_STATUS_COLOR.finalized,
                                                                                                color: '#000000DE',
                                                                                                fontWeight: '500',
                                                                                            }}
                                                                                        />
                                                                                    </TableCell>
                                                                                    <TableCell align="left">
                                                                                        {row?.paymentStatus && (
                                                                                            <Button
                                                                                                variant="text"
                                                                                                color="primary"
                                                                                                sx={{
                                                                                                    padding: '0!important',
                                                                                                    '&:hover': { backgroundColor: 'transparent' },
                                                                                                    '&:focus': { backgroundColor: 'transparent' },
                                                                                                }}
                                                                                                onClick={(event) => {
                                                                                                    event.preventDefault();
                                                                                                    event.stopPropagation();
                                                                                                    if (row?.isDraft) {
                                                                                                        navigate(`/suppliers-invoice/draft/${row?._id}`);
                                                                                                    } else if (
                                                                                                        row?.paymentStatus === 'open' ||
                                                                                                        row?.paymentStatus === 'partial_payment'
                                                                                                    ) {
                                                                                                        // Handle recording payment
                                                                                                        setSelectedId(row?._id);
                                                                                                        setOpenPaymentModal(true);
                                                                                                    } else if (row?.paymentStatus === 'paid_in_full') {
                                                                                                        // If invoice is a draft, navigate to open invoice, otherwise view PDF
                                                                                                        if (row?.isDraft) {
                                                                                                            navigate(`/suppliers-invoice/draft/${row?._id}`);
                                                                                                        } else {
                                                                                                            navigate(`/suppliers-invoice/paid/${row?._id}`);
                                                                                                        }
                                                                                                    }
                                                                                                }}
                                                                                            >
                                                                                                {row?.isDraft
                                                                                                    ? 'Open Invoice'
                                                                                                    : row?.paymentStatus === 'open' ||
                                                                                                        row?.paymentStatus === 'partial_payment'
                                                                                                      ? 'Record Payment'
                                                                                                      : row?.paymentStatus === 'paid_in_full'
                                                                                                        ? row?.isDraft
                                                                                                            ? 'Open Invoice'
                                                                                                            : 'View Invoice'
                                                                                                        : ''}
                                                                                            </Button>
                                                                                        )}
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            ))
                                                                        )}
                                                                    </TableBody>
                                                                </Table>
                                                            </TableContainer>
                                                            <Grid container item alignItems={'center'} justifyContent="end">
                                                                <Grid item>
                                                                    {supplierInvoiceList?.totalCount > 0 && (
                                                                        <TablePagination
                                                                            component="div"
                                                                            count={supplierInvoiceList?.totalCount}
                                                                            page={page}
                                                                            onPageChange={handleChangePage}
                                                                            rowsPerPageOptions={[10, 20, 50, 100]}
                                                                            rowsPerPage={rowsPerPage}
                                                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                                                        />
                                                                    )}
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </Box>
                                        </CustomTabPanel>
                                    );
                                })}
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>

            <RecordPaymentModal
                isLoading={isRecordLoading}
                setIsLoading={setIsRecordLoading}
                open={openPaymentModal}
                selectedId={selectedId}
                onClose={() => {
                    setSelectedId(null);
                    setOpenPaymentModal(false);
                }}
                onSave={async (amount, reference) => {
                    setIsRecordLoading(true);
                    const payload = {
                        supplierInvoice: selectedId,
                        amount: String(formatNumberAsWhole(amount)),
                        notes: reference, // Added reference to payload
                    };
                    const resp = await dispatch(updateRecordPaymentApi(payload));
                    if (resp) {
                        fetchSupplierInvoice();
                        setSelectedId(null);
                        setOpenPaymentModal(false);
                    }
                    setIsRecordLoading(false);
                }}
                paymentRecords={[
                    { amount: 1000, date: 'March 27, 2025', reference: 'lorem ipsum' },
                    { amount: 1000, date: 'March 27, 2025', reference: 'lorem ipsum' },
                ]} // Your payment history data
                totalDue={12.89} // Total amount due
                remainingBalance={348} // Remaining balance
            />

            <Dialog fullScreen={false} open={fullScreenLoader}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default SupplierInvoices;
